import { useSignal } from "@preact/signals";
import type { Notification } from "../../types";
import { formatRelativeDate } from "../../utils/format";
import { plainTextPreview } from "../../utils/mentions";
import { IcoCheck, IcoSpinner } from "../icons";
import { MemberHoverCard } from "./member-hover-card";

interface NotificationDropdownProps {
  notifications: Notification[];
  loading: boolean;
  hasMore: boolean;
  onMarkAllRead: () => void;
  onLoadMore: () => void;
  onNotificationClick: (notification: {
    id: string;
    viewUrl: string;
    type: string;
    targetId: string | null;
    workspaceId: string;
  }) => void;
}

export function NotificationDropdown({
  notifications,
  loading,
  hasMore,
  onMarkAllRead,
  onLoadMore,
  onNotificationClick,
}: NotificationDropdownProps) {
  const hasUnread = notifications.some((n) => !n.readAt);

  return (
    <div class="absolute right-0 top-full mt-2 w-96 max-h-[480px] bg-surface border border-border rounded-lg shadow-lg overflow-hidden z-50">
      {/* Header */}
      <div class="flex items-center justify-between px-4 py-3 border-b border-border">
        <h3 class="font-semibold text-text">Notifications</h3>
        {hasUnread && (
          <button
            type="button"
            class="text-sm text-primary-600 hover:text-primary-700 transition-colors"
            onClick={onMarkAllRead}
          >
            Mark all as read
          </button>
        )}
      </div>

      {/* Notification List */}
      <div class="overflow-y-auto max-h-[380px]">
        {loading && notifications.length === 0 ? (
          <div class="flex items-center justify-center py-12">
            <IcoSpinner class="size-6 text-text-muted animate-spin" />
          </div>
        ) : notifications.length === 0 ? (
          <div class="flex flex-col items-center justify-center py-12 text-text-muted">
            <IcoCheck class="size-8 mb-2" />
            <p>All caught up!</p>
          </div>
        ) : (
          <>
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onClick={() => onNotificationClick(notification)}
              />
            ))}

            {/* Load More */}
            {hasMore && (
              <div class="p-3 border-t border-border">
                <button
                  type="button"
                  class="w-full py-2 text-sm text-primary hover:text-primary-hover transition-colors disabled:opacity-50"
                  onClick={onLoadMore}
                  disabled={loading}
                >
                  {loading ? (
                    <span class="flex items-center justify-center gap-2">
                      <IcoSpinner class="size-4 animate-spin" />
                      Loading...
                    </span>
                  ) : (
                    "Load more"
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

interface NotificationItemProps {
  notification: Notification;
  onClick: () => void;
}

/** Small badge overlay on the avatar indicating notification type. */
function TypeBadge({ type }: { type: string }) {
  if (type === "recognition") {
    return (
      <span class="absolute -bottom-0.5 -right-0.5 size-4.5 rounded-full bg-amber-100 border-2 border-surface flex items-center justify-center">
        <svg
          class="size-2.5 text-amber-600"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      </span>
    );
  }
  if (type === "mention" || type === "everyone") {
    return (
      <span class="absolute -bottom-0.5 -right-0.5 size-4.5 rounded-full bg-blue-100 border-2 border-surface flex items-center justify-center">
        <span class="text-[9px] font-bold text-blue-600 leading-none">@</span>
      </span>
    );
  }
  return null;
}

function NotificationItem({ notification, onClick }: NotificationItemProps) {
  const isUnread = !notification.readAt;
  const imgFailed = useSignal(false);

  const showImg = notification.actorProfilePictureUrl && !imgFailed.value;

  const fallbackAvatar = (
    <div class="size-10 rounded-full bg-surface-muted flex items-center justify-center">
      <span class="text-sm font-medium text-text-muted">
        {notification.actorName?.[0]?.toUpperCase() ?? "?"}
      </span>
    </div>
  );

  const avatar = showImg ? (
    <img
      src={notification.actorProfilePictureUrl!}
      alt=""
      class="size-10 rounded-full object-cover"
      onError={() => {
        imgFailed.value = true;
      }}
    />
  ) : (
    fallbackAvatar
  );

  return (
    <button
      type="button"
      class={`w-full text-left px-4 py-3 hover:bg-surface-hover transition-colors border-b border-border last:border-b-0 ${
        isUnread ? "bg-surface-accent" : ""
      }`}
      onClick={onClick}
    >
      <div class="flex gap-3">
        {/* Actor Avatar with type badge */}
        <div class="flex-shrink-0 relative">
          {notification.actorMemberId ? (
            <MemberHoverCard
              memberId={notification.actorMemberId}
              workspaceId={notification.workspaceId}
            >
              <div class="cursor-pointer">{avatar}</div>
            </MemberHoverCard>
          ) : (
            avatar
          )}
          <TypeBadge type={notification.type} />
        </div>

        {/* Content */}
        <div class="flex-1 min-w-0">
          <div class="flex items-start gap-2">
            <p class="text-sm text-text font-medium line-clamp-1">
              {notification.title}
            </p>
            {isUnread && (
              <span class="flex-shrink-0 size-2 rounded-full bg-primary mt-1.5" />
            )}
          </div>
          {notification.body && (
            <p class="text-sm text-text-muted line-clamp-2 mt-0.5">
              {plainTextPreview(notification.body, 200)}
            </p>
          )}
          <p class="text-xs text-text-muted mt-1">
            {formatRelativeDate(new Date(notification.createdAt))}
          </p>
        </div>
      </div>
    </button>
  );
}
